/**
 * The prompt, from anywhere — the IDE's command box, docked along the bottom of every screen: 96% of
 * the width, 4px above the edge. It starts open; the person can fold it to a bar, and it opens again
 * by itself when an answer arrives, so the result is never missed. Ctrl+J opens it and puts the
 * cursor in it from any screen.
 *
 * It is there for every developer, in the browser and the desktop app. With an agent listening on
 * remote control (Claude Code's hook) it goes straight to that agent. Otherwise — remote control
 * off, nobody listening, or an agent with no remote control of its own — it goes to the **prompt
 * queue**, which the desktop app runs within 30 seconds with whatever agent the person uses there.
 *
 * Two ways to send: to one agent (picked when several are listening), or as team work — a Builder
 * and a Reviewer from the team, in the default flow. It can review the prompt first. When the
 * desktop's own AI ran the last command, its answer is shown in tabs: the reply, the files it
 * created (kept apart, as asked), the files it changed, and the log — plus the queue, and the pinned
 * instructions that go before every prompt.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppWindow, PictureInPicture2, Pin, PinOff, MoreHorizontal, ArrowLeft, ArrowUp, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, PanelBottom, PanelRight, GitCompare, GripHorizontal, Hourglass, Maximize2, Minimize2, ListOrdered, Loader2, MessageSquareText, MonitorPlay, Timer, Plus, RotateCcw, Sparkles, Square, TerminalSquare, Users, Wrench, X, FileText, Search, ListChecks, Wand2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { useSimpleMode } from '../lib/simpleMode'
import { FileShortcuts } from './FileShortcuts'
import { useSuperSkills } from '../lib/teamSuperSkills'
import { DOWNLOAD_URL } from '../lib/startPath'
import { formatRelative, formatTime } from '../lib/format'
import { setFused, setPinned, toggleFused, useFused, usePinned } from '../lib/fuse'
import { isDesktop, repoFiles, repoRead, skillsList } from '../lib/desktop'
import { activeAgents, agentLabel, latestReply, resolveTarget } from '../lib/promptTargets'
import { clearResult, discardLane, newConversation, readRunnerSettings, runHere, stopRunnerFor, useRunnerStatus, withdrawWaiting } from '../lib/runnerHost'
import type { StreamItem } from '../lib/localRunner'
import { AGENT_NAMES, type LocalAgentId } from '../lib/localAgent'
import { useActiveRepository } from '../lib/activeRepository'
import type { QueuedPrompt, SpendBlock } from '../lib/types'
import { PromptReviewDialog } from './PromptReviewDialog'
import { Markdown } from './Markdown'
import { useWhoDoesIt } from './WhoDoesIt'
import { LocalChat } from './LocalChat'
import { AgentModelPicker } from './AgentModelPicker'
import { TerminalPanel } from './TerminalPanel'
import { ChangesPanel } from './ChangesPanel'
import { applySuggestion, CATALOG_COMMANDS, LANE_NAMES_KEY, parse, PROJECT_ACTION_EVENT, PUBLISH_ACTION_EVENT, readLaneNames, RUN_ACTION_EVENT, suggestions, type Target } from '../lib/composer'
import { usePromptCatalog } from '../lib/prompts'
import { readLocalInstructions } from '../lib/localInstructions'
import { Dropdown, DropdownItem } from './Dropdown'
import { readableReply } from '../lib/agentReply'
import { formatInterval } from '../lib/workInterval'
import { Modal } from './Modal'
import SchedulePicker from './SchedulePicker'
import { cronOf, DEFAULT_SCHEDULE, describeSchedule, scheduleRequest, type ScheduleDraft } from '../lib/schedule'
import { appIsAway, notify, shouldNotifyRunEnded } from '../lib/inboxNotify'
import { PREVIEW_EVENT, usePreview } from '../lib/preview'
import { PreviewPanel } from './PreviewPanel'
import { AgentNotes, TaskConversation } from './TaskConversation'
import { folderFor } from '../lib/repoFolders'
import { laneOf, MAX_LANES, nextLane, readLanes, saveLanes, withoutLane } from '../lib/agentLanes'
import { COMPOSE_EVENT, DOCK_EVENT, SEND_EVENT, SUPER_SKILLS_EVENT, sendCommand, type SendDetail, moveTo, resizeTo, sideWidth, type DockGeometry, type DockPlace } from '../lib/dockPlacement'
import { useDockSlot, useSlotRect } from '../lib/dockSlot'
import { BeforeEachCommand } from './BeforeEachCommand'
import { SuperSkills } from './SuperSkills'
import { pickFilePath } from '../lib/files'

const TARGET_KEY = 'devpulse.promptDock.target'
const REVIEW_KEY = 'devpulse.promptDock.reviewFirst'
/** "Auto documentar" (docs/refactor/24): each request written as a PRD in the Documents, then carried out by it. Off by default. */
const AUTO_DOC_KEY = 'devpulse.promptDock.autoDoc'

/** A spending limit stops a run here before it starts (docs/refactor/51, §6.3). */
class SpendLimitReached extends Error {
  constructor(readonly block: SpendBlock) {
    super('spend-limit')
  }
}
const OPEN_KEY = 'devpulse.promptDock.open'
/** Along the right edge (docs/refactor/29) — where it stands unless the person put it at the bottom ('0'), on the web
 *  and in the desktop app alike (owner, 10/10/2026); kept on this computer, with its width. A phone has no room for a
 *  column: there it stays along the bottom whatever this says (styles/components.css). */
const SIDE_KEY = 'devpulse.promptDock.side'
const SIDE_WIDTH_KEY = 'devpulse.promptDock.sideWidth'
/** The height the person dragged the text field to; none: it grows with the text. */
const FIELD_HEIGHT_KEY = 'devpulse.promptDock.fieldHeight'
const FIELD_MIN = 40
const NARROW = '(max-width: 860px)'

type Cadence =
  | null
  | { kind: 'UntilDone' }
  | { kind: 'Scheduled'; every: number }
  | { kind: 'At'; at: string }
  /** "Repetir em horários…" (docs/refactor/66): clock times — every day, weekdays, chosen days, a cron. */
  | { kind: 'Clock'; draft: ScheduleDraft }

/** The next whole hour, as a datetime-local value: "num horário" starts from there. */
function nextHourLocal(): string {
  const d = new Date()
  d.setMinutes(0, 0, 0)
  d.setHours(d.getHours() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** The open project's files, for "Antes de cada comando" to find the instruction files among them. */
const listRepoFiles = (folder: string): Promise<string[]> => repoFiles(folder).then((listed) => listed.files)

type Tab = 'reply' | 'changes' | 'log' | 'queue' | 'along' | 'preview' | 'task'

/** The last task with several agents sent from here (docs/refactor/15): its conversation stays in the box. */
const TASK_KEY = 'devpulse.agentTask'
/** This computer's id (lib/runnerHost.ts): the parts of a task run where its orchestrator runs. */
const MACHINE_KEY = 'devpulse.machineId'


function stored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function store(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Private storage: the dock just forgets between visits.
  }
}

export function PromptDock() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const desktop = isDesktop()
  // The old target select (before 07/10) left its choice in this browser, and no screen can change it
  // any more: honoured, it sent every web prompt to the desktop app's queue even with the IDE's Claude
  // listening. @ chooses now; the leftover is forgotten.
  useEffect(() => {
    try {
      localStorage.removeItem(TARGET_KEY)
    } catch {
      // Private storage: nothing was kept anyway.
    }
  }, [])
  const navigate = useNavigate()
  // ---- the person's agents side by side: "Agente 1", "Agente 2"…, each with its own text and answer
  const [lanes, setLanesState] = useState<number[]>(readLanes)
  const [lane, setLane] = useState(1)
  const [texts, setTexts] = useState<Record<number, string>>({})
  const text = texts[lane] ?? ''
  // The field's height: grows with the text up to 200 px, or the height the person dragged it to (owner, 12/10/2026),
  // kept on this computer; a double click on the handle gives it back to the text.
  const [fieldHeight, setFieldHeight] = useState<number | null>(() => Number(stored(FIELD_HEIGHT_KEY)) || null)
  useLayoutEffect(() => {
    const field = area.current
    if (!field) return
    if (fieldHeight) {
      field.style.maxHeight = 'none'
      field.style.height = `${fieldHeight}px`
      return
    }
    field.style.maxHeight = ''
    field.style.height = 'auto'
    field.style.height = `${Math.min(field.scrollHeight, 200)}px`
  }, [text, lane, fieldHeight])
  const fieldDrag = useRef<{ y: number; from: number } | null>(null)
  const onFieldDrag = (event: React.PointerEvent) => {
    const drag = fieldDrag.current
    if (!drag) return
    // The handle is the field's top edge: up makes it taller.
    const next = Math.round(Math.min(Math.max(drag.from + drag.y - event.clientY, FIELD_MIN), window.innerHeight * 0.45))
    setFieldHeight(next)
    store(FIELD_HEIGHT_KEY, String(next))
  }
  const setText = (value: string) => setTexts((current) => ({ ...current, [lane]: value }))
  const setLanes = (next: number[]) => {
    setLanesState(next)
    saveLanes(next)
  }
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)
  // 'chat' — a conversation with the AI on this machine (desktop only): see LocalChat.
  // The prompt box itself, the free chat with the AI on this machine, or the terminal (`!command`).
  const [mode, setMode] = useState<'agent' | 'chat' | 'terminal'>('agent')
  const [reviewFirst, setReviewFirst] = useState(() => stored(REVIEW_KEY) === '1')
  const [reviewing, setReviewing] = useState(false)
  const [autoDoc, setAutoDoc] = useState(() => stored(AUTO_DOC_KEY) === '1')
  // Both options live behind Enviar's arrow (51, D1), remembered as before.
  const toggleReview = (on: boolean) => {
    setReviewFirst(on)
    store(REVIEW_KEY, on ? '1' : '0')
  }
  const toggleAutoDoc = (on: boolean) => {
    setAutoDoc(on)
    store(AUTO_DOC_KEY, on ? '1' : '0')
  }
  // A request already wrapped by Analisar or Documentar: Auto documentar leaves it as it is.
  const wrapped = useRef(false)
  // "Quem faz?" for Analisar (components/WhoDoesIt.tsx): who was chosen, for the next send only.
  const forced = useRef<Target | null>(null)
  // How the next request goes (docs/refactor/06-compositor.md): at once (the default), until it is
  // done, or again every so often — the last two as an order in Agendados e em equipe.
  const [cadence, setCadence] = useState<Cadence>(null)
  // The "Repetir em horários…" picker, open with the draft being chosen.
  const [clockDraft, setClockDraft] = useState<ScheduleDraft | null>(null)
  // How many agents work on the next request (docs/refactor/15-varios-agentes.md): this one alone, or
  // 2 or 3 — this one orchestrating, the others taking the parts it gives them.
  const [agents, setAgents] = useState(1)
  // Teammates whose agent joins the next task (docs/refactor/15, phase 2): they take the last agent numbers.
  const [mates, setMates] = useState<Array<{ developerId: string; name: string }>>([])
  const [taskId, setTaskId] = useState<string | null>(() => stored(TASK_KEY))
  const [tab, setTab] = useState<Tab>('reply')
  const area = useRef<HTMLTextAreaElement>(null)
  const box = useRef<HTMLElement>(null)

  // ---- open or folded; docked, floating or closed. Every time the app opens, the box is docked
  // along the bottom and open; the page leaves room for it while it is docked.
  // On a phone it opens folded, a bar along the bottom: open, it would cover half the screen.
  const [open, setOpenState] = useState(() => !(typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 560px)').matches))
  const [place, setPlace] = useState<DockPlace>(() => (stored(SIDE_KEY) === '0' ? 'docked' : 'side'))
  const [sideW, setSideW] = useState(() => sideWidth(Number(stored(SIDE_WIDTH_KEY)) || 440, window.innerWidth))
  // A narrow window (a phone) has no room for a column: there the box stays along the bottom.
  const [narrow, setNarrow] = useState(() => typeof window.matchMedia === 'function' && window.matchMedia(NARROW).matches)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(NARROW)
    const onChange = () => setNarrow(query.matches)
    query.addEventListener?.('change', onChange)
    return () => query.removeEventListener?.('change', onChange)
  }, [])
  const side = place === 'side' && !narrow
  // Along the right edge or back along the bottom: remembered for the next time the app opens.
  const putSide = (on: boolean) => {
    setFull(false)
    setPlace(on ? 'side' : 'docked')
    store(SIDE_KEY, on ? null : '0')
  }
  // Only the width changes along the right edge: dragging its left border.
  const sideDrag = useRef(false)
  const onSideResize = (event: React.PointerEvent) => {
    if (!sideDrag.current) return
    const w = sideWidth(window.innerWidth - event.clientX, window.innerWidth)
    setSideW(w)
    store(SIDE_WIDTH_KEY, String(w))
  }
  // On Agentes › Conversas the box is the screen: laid over the place it marks (lib/dockSlot.ts),
  // always open, nothing to drag, fold or close.
  const slot = useDockSlot()
  const slotRect = useSlotRect(slot)
  const page = Boolean(slot && slotRect)
  const [geo, setGeo] = useState<DockGeometry>({ x: 80, y: 120, w: 760, h: 420 })
  const dragging = useRef<{ kind: 'move' | 'size'; startX: number; startY: number; from: DockGeometry; moved: boolean } | null>(null)
  // The menu bar's "Prompt": back where it always opens.
  useEffect(() => {
    const onDock = () => {
      setPlace(stored(SIDE_KEY) === '0' ? 'docked' : 'side')
      setFull(false)
      setOpenState(true)
      store(OPEN_KEY, '1')
      setTimeout(() => area.current?.focus(), 0)
    }
    window.addEventListener(DOCK_EVENT, onDock)
    return () => window.removeEventListener(DOCK_EVENT, onDock)
  }, [])
  // "Mandar para este computador" (Suas máquinas): the box opens with "@its-name " in it.
  useEffect(() => {
    const onCompose = (event: Event) => {
      const start = String((event as CustomEvent<string>).detail ?? '')
      setPlace((current) => (current === 'closed' ? 'docked' : current))
      setOpenState(true)
      store(OPEN_KEY, '1')
      setTexts((current) => ({ ...current, [lane]: start }))
      setTimeout(() => {
        const field = area.current
        field?.focus()
        field?.setSelectionRange(start.length, start.length)
      }, 0)
    }
    window.addEventListener(COMPOSE_EVENT, onCompose)
    return () => window.removeEventListener(COMPOSE_EVENT, onCompose)
  }, [lane])
  const view = () => ({ w: window.innerWidth, h: window.innerHeight })
  // Grabbing the title bar (not a button in it) or the corner: the box floats from where it is.
  const startDrag = (kind: 'move' | 'size', event: React.PointerEvent) => {
    if (event.button !== 0 || full || page || side) return
    if (kind === 'move' && (event.target as HTMLElement).closest('button, select, input, a')) return
    const rect = box.current?.getBoundingClientRect()
    if (!rect) return
    const from = place === 'floating' ? geo : { x: rect.left, y: rect.top, w: rect.width, h: Math.max(rect.height, 240) }
    dragging.current = { kind, startX: event.clientX, startY: event.clientY, from, moved: false }
    ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
  }
  const onDrag = (event: React.PointerEvent) => {
    const drag = dragging.current
    if (!drag) return
    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 4) return
    drag.moved = true
    if (place !== 'floating') setPlace('floating')
    setGeo(drag.kind === 'move' ? moveTo(drag.from, drag.from.x + dx, drag.from.y + dy, view()) : resizeTo(drag.from, drag.from.w + dx, drag.from.h + dy, view()))
  }
  const endDrag = (event: React.PointerEvent) => {
    const drag = dragging.current
    dragging.current = null
    ;(event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId)
    // A drag is not a click: the folded bar does not open when it was only moved.
    if (drag?.moved) suppressClick.current = true
  }
  const suppressClick = useRef(false)
  // Full screen: the whole window for the prompt and its answer; Esc or the same button brings it back.
  const [full, setFull] = useState(false)
  useEffect(() => {
    if (!full) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector('.review-full')) setFull(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [full])
  // "Fundir" (docs/refactor/62): the window is only this box — the prompt and its answer — always in front of Windows.
  const fused = useFused()
  const alwaysInFront = usePinned()
  useEffect(() => {
    if (!fused) return
    setOpenState(true)
    setFull(false)
    setMode('agent')
    setTab('reply')
  }, [fused])
  const setOpen = (value: boolean) => {
    setOpenState(value)
    store(OPEN_KEY, value ? '1' : '0')
  }
  useEffect(() => {
    const element = box.current
    const root = document.documentElement
    // Along the right edge, the page makes room beside it — as wide as it is, or its folded strip.
    if (side && !page && !full) root.style.setProperty('--dock-side', `${open ? sideW : 44}px`)
    else root.style.removeProperty('--dock-side')
    // Only docked does it take room from the page; floating it sits over it, closed it is gone.
    if (!element || (place !== 'docked' && !(place === 'side' && narrow)) || page || typeof ResizeObserver === 'undefined') {
      root.style.removeProperty('--dock-height')
      return
    }
    const observer = new ResizeObserver(() => root.style.setProperty('--dock-height', `${element.offsetHeight + 8}px`))
    observer.observe(element)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--dock-height')
    }
  })

  // ---- whom to send to
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  // The server's prompts behind /testar and /commit (the catalog; never written in the app).
  const { data: catalog } = usePromptCatalog()
  const { data: remote } = useQuery({
    queryKey: ['remote'],
    queryFn: api.remote.overview,
    enabled: Boolean(me?.developerId),
    retry: false,
    refetchInterval: 5_000,
  })
  const active = useMemo(() => activeAgents(remote?.agents ?? []), [remote])
  const target = resolveTarget(active, null)
  const reply = latestReply(remote?.messages ?? [], target)
  const { active: repository, linked: linkedRepositories, choose: chooseRepository } = useActiveRepository()
  // "Qualquer projeto — o computador escolhe" (docs/refactor/29): sent with no project, the computer that takes it decides.
  const [anyProject, setAnyProject] = useState(false)
  const queueRepositoryId = anyProject ? null : repository?.id ?? null
  const who = useWhoDoesIt(repository ? { id: repository.id, name: repository.name } : null)
  const { data: teammates } = useQuery({
    queryKey: ['teammates', repository?.id ?? null],
    queryFn: () => api.workOrders.teammates(repository?.id ?? null),
    staleTime: 60_000,
    retry: false,
    enabled: Boolean(me?.developerId),
  })
  const callable = (teammates ?? []).filter((m) => !m.isMe && m.acceptsTeamWork && (m.desktopOn || m.remoteOn))

  // ---- the prompt queue, for agents with no remote control of their own
  const { data: queue } = useQuery({
    queryKey: ['prompt-queue'],
    queryFn: api.promptQueue.list,
    enabled: Boolean(me?.developerId),
    retry: false,
    refetchInterval: 5_000,
  })
  const { data: pinned } = useQuery({ queryKey: ['docs', 'pinned'], queryFn: api.docs.pinned, enabled: Boolean(me?.developerId), staleTime: 60_000 })
  // ■: a waiting prompt is withdrawn; a running one stops — at once on this desktop, and on another
  // machine when its runner sees it cancelled.
  const stopQueued = useMutation({
    mutationFn: async (item: QueuedPrompt) => {
      if (item.status === 'Running' && repository) stopRunnerFor(repository.name, laneOf(item))
      await api.promptQueue.cancel(item.id)
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['prompt-queue'] }),
  })
  // Notes between the person's agents (docs/refactor/15), shown under "Entre agentes".
  const { data: notesData } = useQuery({ queryKey: ['agent-notes'], queryFn: api.agentTasks.notes, refetchInterval: 15_000, retry: false })
  const notes = notesData ?? []
  const cancelQueued = useMutation({
    mutationFn: (id: string) => api.promptQueue.cancel(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['prompt-queue'] }),
  })
  // A command waiting on the channel for the IDE's Claude (docs/refactor/61) can be taken back too.
  const cancelRemote = useMutation({
    mutationFn: (id: string) => api.remote.cancel(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['remote'] }),
  })
  // "Desfazer este turno" from anywhere: the computer that ran it does it on its next ask (docs/refactor/02).
  const undoQueued = useMutation({
    mutationFn: (id: string) => api.promptQueue.undo(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['prompt-queue'] }),
  })
  // Nobody listening on remote control, or the person chose the queue: the desktop app runs it.
  // Agents 2, 3… always go through it: each is a new agent the desktop app starts beside the first.
  // Kids mode (docs/refactor/48): the plain box, and every request taught (tutor.kids).
  const kids = me?.kidsMode === true
  // Modo simples (51, N5): the person's own choice — the box and Enviar, the Super skills, no architecture words.
  const simple = useSimpleMode() && !kids
  // The super skills' menus come with the curated catalog (docs/refactor/50), and the team's own after them (51, §6.2);
  // a server from before has none, and the button hides.
  const { skills: superSkillList, teamPrompt } = useSuperSkills(!kids)
  // (Where a prompt goes — viaQueue — is decided below, once the person's computers are known: docs/refactor/61.)
  // What the answer tabs show: the chosen agent's, in agent mode; the first agent's otherwise.
  const viewLane = mode === 'agent' ? lane : 1

  // ---- what the desktop's own AI did last
  const runner = useRunnerStatus()
  const lastRun = useMemo(
    () => [...runner].filter((r) => r.result && (r.lane ?? 1) === viewLane).sort((a, b) => (b.result!.finishedAt > a.result!.finishedAt ? 1 : -1))[0],
    [runner, viewLane],
  )
  const result = lastRun?.result

  // What goes with every request from here (docs/refactor/21): the project's, this machine's, the skills on.
  const skillsFolder = desktop && repository ? folderFor(repository.name) : null
  const { data: pinnedForDock } = useQuery({ queryKey: ['docs', 'pinned'], queryFn: api.docs.pinned, enabled: Boolean(me?.developerId), staleTime: 60_000 })
  const { data: skillsHere } = useQuery({ queryKey: ['machine-skills', skillsFolder], queryFn: () => skillsList(skillsFolder), enabled: Boolean(skillsFolder), staleTime: 60_000 })
  const withEvery = [
    ...((pinnedForDock?.length ?? 0) > 0 ? [t('dock.withEvery.project', { count: pinnedForDock!.length })] : []),
    ...(readLocalInstructions().trim() ? [t('dock.withEvery.local')] : []),
    ...((skillsHere ?? []).filter((x) => x.enabled).length > 0 ? [t('dock.withEvery.skills', { count: (skillsHere ?? []).filter((x) => x.enabled).length })] : []),
  ]
  // "Antes de cada comando" counts the same three; the files the agent reads by itself are listed inside it.
  const alongCount = (pinnedForDock?.length ?? 0) + (desktop && readLocalInstructions().trim() ? 1 : 0) + (skillsHere ?? []).filter((x) => x.enabled).length
  // ---- rodar aqui: on the desktop, with the project's folder here, a prompt for an agent runs on this
  // machine at once, in that agent's conversation — no queue (docs/refactor/01-rodar-aqui.md). Only a
  // prompt aimed at a particular agent listening elsewhere (remote control) goes there instead.
  const hereFolder = desktop && repository ? folderFor(repository.name) : null
  const runsHere = mode === 'agent' && Boolean(hereFolder)
  const here = repository ? runner.find((r) => r.repository === repository.name && (r.lane ?? 1) === lane) : undefined
  const hereWorking = runsHere && here?.state === 'working'

  // ---- one composer (docs/refactor/06-compositor.md): @ chooses who, / is a command, ! the terminal
  const listeners = useMemo(() => active.map((a) => ({ id: a.clientSessionId, label: agentLabel(a) })), [active])
  // The person's other computers that are on: "@casa-pc" sends there (docs/refactor/10-paridade-web-celular.md).
  const { data: machineList } = useQuery({ queryKey: ['machines'], queryFn: api.promptQueue.machines, enabled: Boolean(me?.developerId), refetchInterval: 30_000, retry: false })
  const machines = useMemo(() => {
    const self = stored('devpulse.machineId')
    return (machineList ?? []).filter((m) => m.online && m.name && m.id !== self).map((m) => ({ id: m.id, name: m.name! }))
  }, [machineList])
  // Nobody listening: the desktop app's queue — unless this person never had the app (an IDE plugged in by the MCP
  // alone, docs/refactor/61): then the channel, where the Claude of their IDE finds it at the end of its next answer.
  const noDesktop = queue !== undefined && !queue.desktopSeenAt && machineList !== undefined && machineList.length === 0
  const nextTurn = Boolean(remote?.enabled) && active.length === 0 && noDesktop && !(mode === 'agent' && lane > 1)
  const viaQueue = !remote?.enabled || (active.length === 0 && !noDesktop) || (mode === 'agent' && lane > 1)
  const composing = useMemo(() => ({ desktop, machines }), [desktop, machines])
  const parsed = parse(text, listeners, composing)
  const intent: Target = parsed.kind === 'prompt' ? parsed.target : { kind: 'default' }
  const [cursor, setCursor] = useState(0)
  const [picked, setPicked] = useState(0)
  const [hideHints, setHideHints] = useState(false)
  const offered = hideHints ? [] : suggestions(text, cursor, listeners, composing)
  const [terminalRun, setTerminalRun] = useState<{ command: string; nonce: number } | null>(null)
  const [laneNames, setLaneNames] = useState<Record<number, string>>(() => readLaneNames(typeof localStorage === 'undefined' ? null : localStorage))
  const [naming, setNaming] = useState<number | null>(null)
  // The chip says the agent's place among the open tabs ("Agente 2" for the second), whatever its lane
  // number underneath: closing lane 2 must not leave "Agente 1 · Agente 3" on the screen (41, A6).
  const laneName = (n: number) => laneNames[n] || t('dock.lane', { n: lanes.indexOf(n) + 1 })
  const renameLane = (n: number, name: string) => {
    const next = { ...laneNames, [n]: name.trim().slice(0, 24) }
    if (!name.trim()) delete next[n]
    setLaneNames(next)
    store(LANE_NAMES_KEY, JSON.stringify(next))
    setNaming(null)
  }

  /** A command typed after /: each one is something the app already does, where it already does it. */
  const runSlash = (name: string) => {
    if (name === 'executar') window.dispatchEvent(new CustomEvent(RUN_ACTION_EVENT))
    if (name === 'publicar') window.dispatchEvent(new CustomEvent(PUBLISH_ACTION_EVENT))
    else if (name === 'analisar' || name === 'o-que-falta' || name === 'pendencias') window.dispatchEvent(new CustomEvent(PROJECT_ACTION_EVENT, { detail: name }))
    else if (name === 'auditar') navigate('/project/quality')
    else if (name === 'nova' && repository) newConversation(repository.name, lane)
  }

  // Ctrl+J puts the cursor in the box from any screen.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault()
        setOpenState(true)
        store(OPEN_KEY, '1')
        setPlace((current) => (current === 'closed' ? 'docked' : current))
        setTimeout(() => area.current?.focus(), 0)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // "Rodar testes" in Mudanças, a command in Ctrl+K: sent through this box as if typed here.
  const sendRef = useRef<(text: string) => void>(() => {})
  // The words shown for a text sent from another screen, when it is instructions behind a button (SendDetail).
  const shownRef = useRef<string | undefined>(undefined)
  // The same words for it while it waits in the queue or with a listening agent: what was sent → what is shown.
  const shownFor = useRef(new Map<string, string>())
  useEffect(() => {
    const onSend = (event: Event) => {
      const detail = (event as CustomEvent<SendDetail>).detail
      const text = String(detail?.text ?? '').trim()
      if (!text) return
      setPlace((current) => (current === 'closed' ? 'docked' : current))
      setOpenState(true)
      shownRef.current = detail.shown
      sendRef.current(text)
    }
    window.addEventListener(SEND_EVENT, onSend)
    return () => window.removeEventListener(SEND_EVENT, onSend)
  }, [])

  // Super skills (docs/refactor/50): the menu of skills used by a button — from here, or straight at one from Ctrl+K.
  const [superSkills, setSuperSkills] = useState<{ initial: string | null } | null>(null)
  useEffect(() => {
    const onOpen = (event: Event) => {
      setPlace((current) => (current === 'closed' ? 'docked' : current))
      setOpenState(true)
      setSuperSkills({ initial: (event as CustomEvent<string | null>).detail ?? null })
    }
    window.addEventListener(SUPER_SKILLS_EVENT, onOpen)
    return () => window.removeEventListener(SUPER_SKILLS_EVENT, onOpen)
  }, [])

  // The preview (docs/refactor/11-preview.md): a run brought something up here — the box opens on it.
  const preview = usePreview()
  useEffect(() => {
    const onPreview = () => {
      setPlace((current) => (current === 'closed' ? 'docked' : current))
      setOpenState(true)
      setTab('preview')
    }
    window.addEventListener(PREVIEW_EVENT, onPreview)
    return () => window.removeEventListener(PREVIEW_EVENT, onPreview)
  }, [])
  useEffect(() => {
    if (!preview) setTab((current) => (current === 'preview' ? 'reply' : current))
  }, [preview])

  type Sent = 'here' | 'queue' | 'listener' | 'nextTurn' | 'slash' | 'shell' | 'order'
  const send = useMutation({
    // The text to send: the box's, or the one chosen in the review (the original or the suggestion).
    mutationFn: async (chosen?: string): Promise<{ how: Sent; to?: string }> => {
      let shown = shownRef.current
      shownRef.current = undefined
      if (shown) shownFor.current.set((chosen ?? text).trim(), shown)
      const what = parse((chosen ?? text).trim(), listeners, composing)
      if (what.kind === 'shell') {
        setMode('terminal')
        setTerminalRun({ command: what.command, nonce: Date.now() })
        return { how: 'shell' }
      }
      if (what.kind === 'slash') {
        if (!what.command) throw new Error(`unknown-command:${what.name}`)
        const key = CATALOG_COMMANDS[what.command.name]
        if (!key) {
          runSlash(what.command.name)
          return { how: 'slash' }
        }
      }
      // /testar, /commit: the catalog's prompt, sent as if written here.
      const catalogKey = what.kind === 'slash' && what.command ? CATALOG_COMMANDS[what.command.name] : undefined
      const fromCatalog = catalogKey ? catalog?.prompts.find((p) => p.key === catalogKey)?.body : undefined
      if (catalogKey && !fromCatalog) throw new Error('catalog-missing')
      const picked = forced.current
      forced.current = null
      const asPrompt = what.kind === 'prompt' ? (picked ? { ...what, target: picked } : what) : { kind: 'prompt' as const, target: { kind: 'default' } as Target, text: fromCatalog! }
      let body = asPrompt.text
      // Auto documentar: the request becomes "write the PRD, then do it by the PRD" (catalog request.prd-run).
      const typed = what.kind === 'prompt' && (asPrompt.target.kind === 'default' || asPrompt.target.kind === 'agent' || asPrompt.target.kind === 'machine')
      if (autoDoc && !kids && !simple && typed && !wrapped.current && body) {
        const template = catalog?.prompts.find((p) => p.key === 'request.prd-run')?.body
        if (!template) throw new Error('catalog-missing')
        body = template.split('{{request}}').join(body)
      }
      wrapped.current = false
      // Kids mode (docs/refactor/48): the child's words inside the teacher's prompt (catalog tutor.kids); the box and
      // the answer show their words.
      if (kids && typed && body) {
        const tutor = catalog?.prompts.find((p) => p.key === 'tutor.kids')?.body
        if (!tutor) throw new Error('catalog-missing')
        shown = shown ?? asPrompt.text
        body = tutor.split('{{request}}').join(body)
        shownFor.current.set(body.trim(), shown)
      }
      // "Até terminar" / "Repetir a cada…": an order, followed in Agendados e em equipe.
      if (cadence && body && (asPrompt.target.kind === 'default' || asPrompt.target.kind === 'agent')) {
        await api.workOrders.create({
          kind: cadence.kind === 'At' ? 'Once' : cadence.kind === 'Clock' ? 'Scheduled' : cadence.kind,
          startAt: cadence.kind === 'At' ? new Date(cadence.at).toISOString() : null,
          instructions: body,
          title: body.split(String.fromCharCode(10))[0]!.slice(0, 120),
          repositoryId: repository?.id ?? null,
          ...(cadence.kind === 'Clock' ? scheduleRequest(cadence.draft) : { intervalMinutes: cadence.kind === 'Scheduled' ? cadence.every : null }),
        })
        setCadence(null)
        return { how: 'order' as Sent }
      }
      if (!body) throw new Error('empty')
      // Several agents: the server opens the task and wraps the request in the orchestrator's role; the
      // orchestrator runs as the first agent, and the parts it gives go to the others' lanes.
      let lead = lane
      // From the web, a task's orchestrator runs on the principal computer, when there is one (docs/refactor/23).
      let taskMachine: string | undefined
      // "@time" (docs/refactor/15, phase 3) is the same task as "+ o agente de um colega": you and the teammate picked.
      const teamCall = asPrompt.target.kind === 'team'
      if (teamCall && mates.length === 0) throw new Error('team-no-mate')
      const count = teamCall ? Math.max(agents, 1 + mates.length) : agents
      if (count > 1 && (teamCall || asPrompt.target.kind === 'default' || asPrompt.target.kind === 'agent' || asPrompt.target.kind === 'machine')) {
        const created = await api.agentTasks.create({
          text: body,
          repositoryId: repository?.id ?? null,
          agents: count,
          machine: asPrompt.target.kind === 'machine' ? asPrompt.target.id : desktop ? stored(MACHINE_KEY) : null,
          // The teammates' agents are the last numbers: 3 of 3, or 2 and 3.
          ...(mates.length > 0 ? { teammates: mates.map((m, i) => ({ agent: count - mates.length + 1 + i, developerId: m.developerId })) } : {}),
        })
        body = created.prompt
        lead = 1
        if (!desktop && created.machine) taskMachine = created.machine
        setTaskId(created.id)
        store(TASK_KEY, created.id)
        setAgents(1)
        const own = created.agents - mates.length
        setMates([])
        // The person's own agents' lanes, so they can watch each one work (a teammate's runs on their computer).
        const wanted = Array.from({ length: own }, (_, i) => i + 1)
        if (wanted.some((n) => !lanes.includes(n))) setLanes([...new Set([...lanes, ...wanted])].sort((a, b) => a - b))
      }
      if (asPrompt.target.kind === 'listener') {
        await api.remote.send(body, asPrompt.target.id)
        return { how: 'listener', to: asPrompt.target.id }
      }
      if (asPrompt.target.kind === 'elsewhere') {
        await api.promptQueue.send(body, queueRepositoryId, lane)
        return { how: 'queue' }
      }
      if (asPrompt.target.kind === 'machine') {
        await api.promptQueue.send(body, queueRepositoryId, lead, asPrompt.target.id)
        return { how: 'queue', to: asPrompt.target.name }
      }
      const hereAgent = asPrompt.target.kind === 'agent' ? asPrompt.target.agent : undefined
      if ((runsHere || hereAgent) && repository && hereFolder) {
        // A spending limit at its stop holds here too, though this run never passes the queue (51, §6.3). Unreachable
        // or from before: it runs — a limit is the organization's rule, not a reason to stop working offline.
        const block = await api.usage.spendCheck({ repositoryId: repository.id, agent: hereAgent ?? readRunnerSettings().agent }).catch(() => null)
        if (block) throw new SpendLimitReached(block)
        // Starts now and streams into the answer pane; the box is free again at once. A failure to
        // start (the agent missing, the folder gone) comes back as a notice.
        // A long turn that ends while the person looks elsewhere: the system tells them.
        const startedAt = Date.now()
        runHere(repository.name, body, lead, hereAgent, shown)
          .then((done) => {
            if (done.withdrawn) return
            if (shouldNotifyRunEnded(startedAt, Date.now(), done.code, appIsAway()))
              notify(t(done.code === 0 ? 'dock.runEnded' : 'dock.runFailed'), body.split(String.fromCharCode(10))[0]!.slice(0, 120))
          })
          .catch((error: unknown) =>
          setNotice({ ok: false, text: /no-folder|folder-not-picked/.test(String(error)) ? t('dock.error.noFolder') : t('dock.error.runHere', { reason: String(error).replace(/^Error:\s*/, '') }) }),
        )
        return { how: 'here' }
      }
      if (viaQueue) {
        if (taskMachine) await api.promptQueue.send(body, queueRepositoryId, mode === 'agent' ? lead : 1, taskMachine)
        else await api.promptQueue.send(body, queueRepositoryId, mode === 'agent' ? lead : 1)
        return { how: 'queue' }
      }
      await api.remote.send(body, target)
      return { how: nextTurn ? 'nextTurn' : 'listener', to: target ?? undefined }
    },
    onSuccess: ({ how, to }) => {
      // (A command sent from another screen ends here too: see SEND_EVENT below.)
      setText('')
      setHideHints(false)
      if (how === 'here' || how === 'slash' || how === 'shell') {
        // Nothing to announce: the answer pane, the command's own screen or the terminal shows it.
        setNotice(null)
        if (how === 'here') setTab('reply')
        return
      }
      const listener = to ? active.find((a) => a.clientSessionId === to) : undefined
      setNotice({
        ok: true,
        text: how === 'order'
            ? t('dock.cadence.sent')
          : how === 'queue'
            ? to ? t('dock.sentMachine', { name: to }) : lane > 1 ? t('dock.sentLane', { n: lane }) : t(queue?.desktopOnline ? 'dock.sentQueue' : 'dock.sentQueueOffline')
            : how === 'nextTurn'
            ? t('dock.sentNextTurn')
            : listener ? t('dock.sentTo', { agent: agentLabel(listener) }) : t('dock.sentAny'),
      })
      if (how === 'queue') setTab('queue')
      void queryClient.invalidateQueries({ queryKey: ['remote'] })
      void queryClient.invalidateQueries({ queryKey: ['prompt-queue'] })
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] })
    },
    onError: (error) =>
      setNotice({
        ok: false,
        text: error instanceof SpendLimitReached
          ? t('dock.error.spendLimit', { target: error.block.target, spent: error.block.spent.toFixed(2), stop: error.block.stopUsd.toFixed(2) })
          : error instanceof ApiError && error.code === 'spend_limit'
          ? t('dock.error.spendLimitAny')
          : error instanceof Error && error.message.startsWith('unknown-command:')
          ? t('composer.unknownCommand', { name: error.message.slice('unknown-command:'.length) })
          : error instanceof Error && error.message === 'team-no-mate'
          ? t('dock.team.pick')
          : error instanceof Error && error.message === 'catalog-missing'
          ? t('composer.catalogMissing')
          : error instanceof ApiError && error.code === 'unknown_machine'
          ? t('composer.unknownMachine')
          : error instanceof ApiError && error.code && ['remote_off', 'invalid_lane', 'too_many_pending', 'plan_limit', 'plan_team_only', 'teammate_not_accepting', 'teammate_remote_off', 'not_in_team'].includes(error.code)
          ? t(`work.error.${error.code}`)
          : error instanceof ApiError && error.code === 'not_linked'
            ? t('dock.error.notLinked')
            : t('error.generic'),
      }),
  })

  // An answer that arrives opens it again, even folded: the result is never missed. The answers
  // already there when it loads are not news.
  const answeredAt =
    [result?.finishedAt, reply?.createdAt, (queue?.items ?? []).find((item) => item.completedAt)?.completedAt]
      .filter((at): at is string => Boolean(at))
      .sort()
      .at(-1) ?? null
  const seenAnswer = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!remote || !queue) return
    if (seenAnswer.current === undefined) {
      seenAnswer.current = answeredAt
      return
    }
    if (answeredAt && answeredAt !== seenAnswer.current) {
      seenAnswer.current = answeredAt
      setOpenState(true)
      store(OPEN_KEY, '1')
      setTab('reply')
    }
  }, [answeredAt, remote, queue])

  // Only for a developer: whatever remote control says, the queue takes the prompt.
  if (!me?.developerId || !remote || (place === 'closed' && !page)) return null
  sendRef.current = (text: string) => send.mutate(text)
  const shown = open || page

  // Analisar / Documentar (docs/refactor/24): the typed request, inside the catalog's prompt, sent as usual.
  const sendAs = (key: 'request.analyze' | 'request.prd') => {
    if (!text.trim() || send.isPending) return
    const template = catalog?.prompts.find((p) => p.key === key)?.body
    if (!template) {
      setNotice({ ok: false, text: t('composer.catalogMissing') })
      return
    }
    const body = template.split('{{request}}').join(text.trim())
    // Documentar is written where the person is, as before; only Analisar asks who does it (the owner's request).
    if (key === 'request.prd') {
      setNotice(null)
      wrapped.current = true
      send.mutate(body)
      return
    }
    // Who does it: alone with one agent it goes at once; otherwise the person picks (an agent, a computer, a teammate).
    void who.ask((chosen, option) => {
      setNotice(null)
      if (chosen.builderDeveloperId) {
        api.workOrders
          .create({ kind: 'Once', instructions: body, title: text.trim().split(String.fromCharCode(10))[0]!.slice(0, 120), repositoryId: repository?.id ?? null, builderDeveloperId: chosen.builderDeveloperId })
          .then(() => {
            setText('')
            setNotice({ ok: true, text: t('who.sentTo', { name: option?.label ?? '' }) })
          })
          .catch(() => setNotice({ ok: false, text: t('error.generic') }))
        return
      }
      forced.current = chosen.listener ? { kind: 'listener', id: chosen.listener }
        : chosen.agent && isLocalAgentId(chosen.agent) ? { kind: 'agent', agent: chosen.agent }
          : chosen.machine ? { kind: 'machine', id: chosen.machine, name: option?.label ?? '' }
            : null
      wrapped.current = true
      send.mutate(body)
    })
  }

  const submit = (skipReview = false) => {
    if (!text.trim() || send.isPending) return
    setNotice(null)
    if (reviewFirst && !kids && !simple && !skipReview && parsed.kind === 'prompt') {
      setReviewing(true)
      return
    }
    send.mutate(undefined)
  }

  const queued = queue?.items ?? []
  const laneQueued = queued.filter((item) => laneOf(item) === viewLane)
  const lastQueued = laneQueued.find((item) => item.result)
  // Remote control answers the first agent only: the others are the desktop app's own.
  const replyText = result?.output || (viewLane === 1 ? reply?.text : undefined) || lastQueued?.result || ''
  // A day later the last answer is history: shown dated and quiet, never as a standing "needs you" (41, A2).
  const resultIsOld = Boolean(result && Date.now() - new Date(result.finishedAt).getTime() > 24 * 60 * 60 * 1000)
  // The chosen agent's prompt still waiting or running: with the box empty, ■ stops it.
  const laneActive = mode === 'agent' ? laneQueued.find((item) => item.status === 'Pending' || item.status === 'Running') : undefined
  const stoppable = (Boolean(laneActive) || hereWorking) && !text.trim()
  const stop = () => {
    // Run here: the agent's process ends now. Through the queue: withdrawn, or stopped where it runs.
    if (hereWorking && repository) stopRunnerFor(repository.name, lane)
    else if (laneActive) stopQueued.mutate(laneActive)
  }
  // What the answer pane shows for an agent run here: the run as it happens — from the moment it starts, before
  // the agent's first word (a local AI can take a while) — or the last one.
  const showStream = runsHere && (Boolean(here?.stream?.length) || Boolean(here?.waitingForFolder) || (hereWorking && Boolean(here?.streamPrompt)))
  // The person's requests not answered yet, above the box (the owner, 19/10: "da impressão que o comando some"):
  // the one being worked on and those waiting their turn, here, in the queue or with a listening agent.
  const lastAnswerAt = reply?.createdAt ?? ''
  // A prompt for a project no computer that is on has a folder for waits for nobody: said, not left as "waiting" (docs/refactor/53).
  const onlineMachines = (machineList ?? []).filter((m) => m.online)
  const noFolderFor = (name: string | null) =>
    name && onlineMachines.length > 0 && !onlineMachines.some((m) => m.repositories.some((r) => r.toLowerCase() === name.toLowerCase())) ? name : undefined
  // `from`: a teammate's step, said by name — whose work this computer is about to do (docs/refactor/63, M2).
  const pendingLines: Array<{ key: string; text: string; working: boolean; cancel?: () => void; noFolder?: string; nextTurn?: string; from?: string }> = runsHere
    ? [
        ...(hereWorking && here?.streamPrompt ? [{ key: 'here', text: here.streamPrompt, working: true }] : []),
        ...(here?.waiting ?? []).map((w) => ({ key: w.id, text: w.text, working: false, cancel: w.mine && repository ? () => withdrawWaiting(repository.name, lane, w.id) : undefined })),
      ]
    : [
        ...laneQueued
          .filter((item) => item.status === 'Running' || item.status === 'Pending')
          .sort((a, b) => (a.status === b.status ? (a.createdAt < b.createdAt ? -1 : 1) : a.status === 'Running' ? -1 : 1))
          .map((item) => ({ key: item.id, text: shownFor.current.get(item.prompt.trim()) ?? item.prompt, working: item.status === 'Running', cancel: item.status === 'Pending' ? () => cancelQueued.mutate(item.id) : undefined, noFolder: item.status === 'Pending' ? noFolderFor(item.repository) : undefined, from: item.sentBy ?? undefined })),
        ...(viewLane === 1 ? remote?.messages ?? [] : [])
          .filter((m) => m.direction === 'ToAgent' && (m.status === 'Pending' || (m.status === 'Delivered' && m.createdAt > lastAnswerAt)))
          // One still waiting stays in sight for as long as it may run (a day, sent while nobody listened: docs/refactor/61).
          .filter((m) => m.status === 'Pending' || Date.now() - new Date(m.createdAt).getTime() < 2 * 60 * 60 * 1000)
          .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
          .map((m) => ({
            key: m.id,
            text: shownFor.current.get(m.text.trim()) ?? m.text,
            working: m.status === 'Delivered',
            cancel: m.status === 'Pending' ? () => cancelRemote.mutate(m.id) : undefined,
            // Waiting for the IDE's Claude to finish its next answer: said, with when it stops waiting.
            nextTurn: m.status === 'Pending' && !m.clientSessionId && active.length === 0 ? (m.expiresAt ? formatTime(m.expiresAt, locale) : '') : undefined,
            from: m.sentBy ?? undefined,
          })),
      ]
  const laneBusy = (n: number) =>
    queued.some((item) => laneOf(item) === n && (item.status === 'Pending' || item.status === 'Running'))
    || runner.some((r) => r.repository === repository?.name && (r.lane ?? 1) === n && r.state === 'working')
  const addLane = () => {
    const n = nextLane(lanes)
    if (n === null) {
      setNotice({ ok: false, text: t('dock.laneMax', { max: MAX_LANES }) })
      return
    }
    setLanes([...lanes, n].sort((a, b) => a - b))
    setLane(n)
    setNotice(null)
    setTimeout(() => area.current?.focus(), 0)
  }
  const closeLane = (n: number) => {
    // Its own checkout goes too (ADR-0011); everything it did stays on its branch.
    if (repository) void discardLane(repository.name, n)
    setLanes(withoutLane(lanes, n))
    setTexts(({ [n]: _gone, ...rest }) => rest)
    if (lane === n) setLane(1)
  }
  const laneBar = (
    <div className="prompt-dock__lanes" role="tablist" aria-label={t('dock.lanes')}>
      {lanes.map((n) => (
        <span key={n} className={`prompt-dock__lane${lane === n ? ' is-active' : ''}`}>
          {naming === n ? (
            <input
              className="prompt-dock__lane-name"
              autoFocus
              defaultValue={laneNames[n] ?? ''}
              placeholder={t('dock.lane', { n })}
              aria-label={t('composer.renameLane')}
              maxLength={24}
              onBlur={(event) => renameLane(n, event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') renameLane(n, event.currentTarget.value)
                if (event.key === 'Escape') setNaming(null)
              }}
            />
          ) : (
            <button type="button" role="tab" aria-selected={lane === n} onClick={() => setLane(n)} onDoubleClick={() => setNaming(n)} title={t('composer.renameHint')}>
              {laneBusy(n) && <span className="prompt-dock__lane-dot" aria-hidden />}
              {laneName(n)}
            </button>
          )}
          {n > 1 && (
            <button type="button" className="prompt-dock__lane-close" aria-label={t('dock.closeLane', { n })} title={t('dock.closeLane', { n })} onClick={() => closeLane(n)}>
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      <button type="button" className="prompt-dock__lane-add" onClick={addLane} disabled={lanes.length >= MAX_LANES} aria-label={t('dock.addLane')} title={t('dock.addLane')}>
        <Plus size={13} />
      </button>
    </div>
  )
  const waiting = queued.filter((item) => item.status === 'Pending' || item.status === 'Running').length
  const tabs: Array<{ key: Tab; label: string; count?: number; icon: JSX.Element }> = [
    { key: 'reply', label: t('dock.tab.reply'), icon: <MessageSquareText size={12} /> },
    // What a run brought up on this machine, while there is something to show.
    ...(desktop && preview ? [{ key: 'preview' as Tab, label: t('dock.tab.preview'), icon: <MonitorPlay size={12} /> }] : []),
    ...(desktop
      ? [
          { key: 'changes' as Tab, label: t('dock.tab.changes'), count: result?.changes?.length ?? (result ? result.created.length + result.modified.length : 0), icon: <GitCompare size={12} /> },
        ]
      : []),
    // What goes with every request and what steers the agent, in sight (docs/refactor/50) — not in Modo simples.
    ...(simple ? [] : [{ key: 'along' as Tab, label: t('dock.tab.along'), count: alongCount, icon: <ListChecks size={12} /> }]),
  ]
  // Behind "mais" (41, A5): useful every day to few, a wall to everyone else. The active one surfaces as a tab.
  const moreTabs: Array<{ key: Tab; label: string; count?: number; icon: JSX.Element }> = [
    ...(desktop ? [{ key: 'log' as Tab, label: t('dock.tab.log'), icon: <TerminalSquare size={12} /> }] : []),
    { key: 'queue', label: t('dock.tab.queue'), count: waiting, icon: <ListOrdered size={12} /> },
    // The conversation between the agents of the last task with several of them, and the notes they leave each other.
    ...(taskId || notes.length > 0 ? [{ key: 'task' as Tab, label: t('dock.tab.task'), count: notes.length, icon: <Users size={12} /> }] : []),
  ]
  const moreCount = moreTabs.reduce((sum, item) => sum + (item.count ?? 0), 0)
  const showResult = Boolean(preview || showStream || replyText || result || queued.length > 0 || alongCount > 0 || hereFolder || taskId || notes.length > 0)

  const modeSwitch = (
    <div className="segmented prompt-dock__mode" role="radiogroup" aria-label={t('dock.mode')}>
      {(desktop ? (['agent', 'chat'] as const) : (['agent'] as const)).map((m) => (
        <button key={m} type="button" role="radio" aria-checked={mode === m} className={`segmented__item${mode === m ? ' segmented__item--active' : ''}`} onClick={() => setMode(m)}>
          {t(`dock.mode.${m}`)}
        </button>
      ))}
    </div>
  )

  return (
    <section
      ref={box}
      className={
        fused
          ? `prompt-dock panel is-fused${hereWorking ? ' is-working' : ''}`
          : page
          ? 'prompt-dock panel is-page'
          : `prompt-dock panel${open ? '' : ' is-folded'}${full && open ? ' is-full' : ''}${place === 'floating' && !full ? ' is-floating' : ''}${side && !full ? ' is-side' : ''}${kids ? ' prompt-dock--kids' : ''}${simple ? ' prompt-dock--simple' : ''}`
      }
      style={
        fused
          ? undefined
          : page && slotRect
          ? { left: slotRect.left, top: slotRect.top, width: slotRect.width, height: slotRect.height }
          : place === 'floating' && !full ? { left: geo.x, top: geo.y, width: geo.w, height: open ? geo.h : undefined }
            : side && !full ? { width: open ? sideW : 44 } : undefined
      }
      aria-label={t('dock.title')}
    >
      {side && open && !full && !page && !fused && (
        <div
          className="prompt-dock__side-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label={t('dock.sideResize')}
          onPointerDown={(event) => {
            sideDrag.current = true
            ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
          }}
          onPointerMove={onSideResize}
          onPointerUp={(event) => {
            sideDrag.current = false
            ;(event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId)
          }}
        />
      )}
      <header
        className="prompt-dock__head"
        onClick={() => {
          if (suppressClick.current) {
            suppressClick.current = false
            return
          }
          if (!open) setOpen(true)
        }}
        onPointerDown={(event) => {
          if (!fused) startDrag('move', event)
        }}
        onPointerMove={onDrag}
        onPointerUp={endDrag}
        title={fused ? undefined : t('dock.dragHint')}
      >
        {fused ? <span className="prompt-dock__fused-mark" aria-hidden /> : <GripHorizontal size={14} className="prompt-dock__grip" aria-hidden />}
        <strong className="small">{fused ? t('dock.fusedTitle') : t('dock.title')}</strong>
        {active.length > 0 && <span className="tiny muted">{t('dock.activeCount', { count: active.length })}</span>}
        {waiting > 0 && <span className="tiny prompt-dock__desk is-on">{t('dock.inQueue', { count: waiting })}</span>}
        {!open && notice && <span className="tiny muted truncate">{notice.text}</span>}
        <span className="spacer" />
        {fused ? (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--icon btn--sm"
              aria-pressed={alwaysInFront}
              aria-label={t('dock.pin')}
              title={alwaysInFront ? t('dock.pinOn') : t('dock.pinOff')}
              onClick={(event) => {
                event.stopPropagation()
                void setPinned(!alwaysInFront)
              }}
            >
              {alwaysInFront ? <Pin size={14} /> : <PinOff size={14} />}
            </button>
            <button
              type="button"
              className="btn btn--sm prompt-dock__restore"
              title={t('dock.restoreHint')}
              onClick={(event) => {
                event.stopPropagation()
                void setFused(false)
              }}
            >
              <AppWindow size={14} /> {t('dock.restore')}
            </button>
          </>
        ) : (
          desktop && !page && open && (
            <button
              type="button"
              className="btn btn--ghost btn--sm prompt-dock__fuse"
              title={t('dock.fuseHint')}
              onClick={(event) => {
                event.stopPropagation()
                void toggleFused()
              }}
            >
              <PictureInPicture2 size={14} /> {t('dock.fuse')}
            </button>
          )
        )}
        {!fused && (<>
        {!open && !side && <span className="tiny muted">Ctrl+J</span>}
        {open && !full && !narrow && (
          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm"
            aria-pressed={side}
            aria-label={side ? t('dock.toBottom') : t('dock.toSide')}
            title={side ? t('dock.toBottom') : t('dock.toSide')}
            onClick={(event) => {
              event.stopPropagation()
              putSide(!side)
            }}
          >
            {side ? <PanelBottom size={14} /> : <PanelRight size={14} />}
          </button>
        )}
        {open && (
          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm"
            aria-pressed={full}
            aria-label={full ? t('dock.exitFull') : t('dock.full')}
            title={full ? t('dock.exitFull') : t('dock.full')}
            onClick={(event) => {
              event.stopPropagation()
              setFull(!full)
            }}
          >
            {full ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        )}
        <button
          type="button"
          className="btn btn--ghost btn--icon btn--sm prompt-dock__fold"
          aria-expanded={open}
          aria-label={open ? t('dock.fold') : t('dock.unfold')}
          title={open ? t('dock.fold') : t('dock.unfold')}
          onClick={(event) => {
            event.stopPropagation()
            if (open) setFull(false)
            setOpen(!open)
          }}
        >
          {side ? (open ? <ChevronRight size={15} /> : <ChevronLeft size={15} />) : open ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--icon btn--sm"
          aria-label={t('dock.close')}
          title={t('dock.close')}
          onClick={(event) => {
            event.stopPropagation()
            setFull(false)
            setPlace('closed')
          }}
        >
          <X size={15} />
        </button>
        </>)}
      </header>

      {shown && mode === 'chat' && (
        <div className="prompt-dock__chat">
          {modeSwitch}
          <LocalChat repository={repository ?? null} />
        </div>
      )}

      {shown && mode === 'terminal' && (
        <div className="prompt-dock__chat">
          <button type="button" className="btn btn--ghost btn--sm prompt-dock__back" onClick={() => setMode('agent')}>
            <ArrowLeft size={13} /> {t('composer.backToPrompt')}
          </button>
          {/* Commands in the active repository's folder: PowerShell (or cmd) on Windows, the shell elsewhere. */}
          <TerminalPanel folder={repository ? folderFor(repository.name) : null} run={terminalRun} />
        </div>
      )}

      {shown && mode !== 'chat' && mode !== 'terminal' && (
        <div className="prompt-dock__body">
          <div className="prompt-dock__compose">
          {!fused && modeSwitch}

          {mode === 'agent' && !fused && laneBar}

          {intent.kind === 'team' ? (
            <>
              <p className="tiny muted prompt-dock__note"><span className="prompt-dock__desk is-on">{t('composer.to.team')}</span></p>
              {/* Who joins: the same teammates as "+ o agente de um colega" in 👥. */}
              {callable.length === 0 ? (
                <p className="tiny muted prompt-dock__note">{t('dock.team.nobody')}</p>
              ) : (
                <div className="row row--tight row--wrap prompt-dock__mates" role="group" aria-label={t('dock.team.pick')}>
                  {callable.map((m) => {
                    const chosen = mates.some((x) => x.developerId === m.developerId)
                    return (
                      <button
                        key={m.developerId}
                        type="button"
                        className={`btn btn--sm${chosen ? ' btn--primary' : ''}`}
                        aria-pressed={chosen}
                        onClick={() => {
                          const next = chosen ? mates.filter((x) => x.developerId !== m.developerId) : [...mates, { developerId: m.developerId, name: m.name }].slice(-2)
                          setMates(next)
                          setAgents(next.length > 0 ? 1 + next.length : 1)
                        }}
                      >
                        {m.name}
                      </button>
                    )
                  })}
                </div>
              )}
            </>
          ) : intent.kind === 'listener' ? (
            <p className="tiny muted prompt-dock__note">{t('dock.to', { agent: listeners.find((l) => l.id === intent.id)?.label ?? '' })}</p>
          ) : intent.kind === 'agent' ? (
            <p className="tiny muted prompt-dock__note">
              <span className="prompt-dock__desk is-on">{t('composer.to.agent', { agent: agentName(intent.agent) })}</span>
              {repository && <> · {t('dock.inRepo', { name: repository.name })}</>}
            </p>
          ) : intent.kind === 'machine' ? (
            <p className="tiny muted prompt-dock__note">
              <span className="prompt-dock__desk is-on">{t('composer.to.machine', { name: intent.name })}</span>
              {(repository || anyProject) && <> · <ProjectPick linked={linkedRepositories} chosen={anyProject ? null : repository?.id ?? null} onChoose={(id) => { setAnyProject(id === null); if (id) chooseRepository(id) }} /></>}
            </p>
          ) : intent.kind === 'elsewhere' || (!runsHere && viaQueue) ? (
            <p className="tiny muted prompt-dock__note">
              <span className={queue?.desktopOnline ? 'prompt-dock__desk is-on' : 'prompt-dock__desk'}>
                {queue?.desktopOnline ? t('composer.to.otherMachine') : t('composer.to.whenOn')}
              </span>
              {/* No computer of theirs ever ran the app: nothing will answer until it does — said now, with the way there (51, N4). */}
              {!queue?.desktopOnline && machineList !== undefined && machineList.length === 0 && (
                <> · {t('composer.to.noApp')} <a href={DOWNLOAD_URL} target="_blank" rel="noreferrer">{t('home.getApp.download')}</a></>
              )}
              {(repository || anyProject) && <> · <ProjectPick linked={linkedRepositories} chosen={anyProject ? null : repository?.id ?? null} onChoose={(id) => { setAnyProject(id === null); if (id) chooseRepository(id) }} /></>}
            </p>
          ) : runsHere && repository ? (
            <p className="tiny muted prompt-dock__note">
              <span className="prompt-dock__desk is-on">{t('dock.runsHere', { agent: agentName(readRunnerSettings().agent) })}</span>
              {' '}<AgentModelPicker agent={readRunnerSettings().agent} />
              {' · '}{t('dock.inRepo', { name: repository.name })}
              {here?.worktree && <span title={t('dock.worktreeHint')}>{' · '}{t('dock.worktree', { branch: here.worktree.branch })}</span>}
              {here?.inConversation && <>{' · '}{t('dock.continues')}</>}
              {here?.inConversation && (
                <button type="button" className="btn btn--ghost btn--sm prompt-dock__new" onClick={() => newConversation(repository.name, lane)} disabled={hereWorking}>
                  <RotateCcw size={11} /> {t('dock.newConversation')}
                </button>
              )}
            </p>
          ) : nextTurn ? (
            <p className="tiny muted prompt-dock__note">
              <span className="prompt-dock__desk is-on">{t('composer.to.nextTurn')}</span>
              {' · '}{t('composer.to.noApp')} <a href={DOWNLOAD_URL} target="_blank" rel="noreferrer">{t('home.getApp.download')}</a>
            </p>
          ) : (
            <p className="tiny muted prompt-dock__note">{t('dock.to', { agent: active[0] ? agentLabel(active[0]) : '' })}</p>
          )}

          {/* Modo simples (51, N7): where the answer comes, in plain words, in place of the lines above. */}
          {simple && <p className="tiny muted prompt-dock__simple-note">{t('dock.simpleNote')}</p>}

          {/* One frame, as in an agent's own prompt: the text, and under it the tools and send — which
              turns into ■ while the chosen agent's prompt waits or runs and the box is empty. */}
          {pendingLines.length > 0 && (
            <ul className="prompt-dock__pending" aria-label={t('dock.pending.title')}>
              {pendingLines.slice(0, 4).map((line) => (
                <li key={line.key} className={line.working ? 'is-working' : undefined}>
                  {line.working ? <Loader2 size={12} className="spin" aria-hidden /> : <Hourglass size={12} aria-hidden />}
                  <span
                    className="prompt-dock__pending-state tiny"
                    title={line.noFolder ? t('dock.pending.noFolderHint', { name: line.noFolder }) : line.nextTurn !== undefined ? t(line.nextTurn ? 'dock.pending.nextTurnUntil' : 'dock.pending.nextTurnHint', { time: line.nextTurn }) : undefined}
                  >
                    {line.noFolder ? t('dock.pending.noFolder', { name: line.noFolder }) : line.nextTurn !== undefined ? t('dock.pending.nextTurn') : t(line.working ? 'dock.pending.working' : 'dock.pending.waiting')}
                  </span>
                  {line.from && <span className="tiny muted prompt-dock__pending-from" title={t('dock.pending.fromHint', { name: line.from })}>{t('dock.pending.from', { name: line.from })}</span>}
                  <span className="small truncate prompt-dock__pending-text" title={line.text}>{line.text.split(String.fromCharCode(10))[0]}</span>
                  {line.cancel && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm btn--icon"
                      aria-label={t('dock.pending.cancel', { text: line.text.split(String.fromCharCode(10))[0]!.slice(0, 60) })}
                      title={t('dock.pending.cancel', { text: line.text.split(String.fromCharCode(10))[0]!.slice(0, 60) })}
                      onClick={line.cancel}
                    >
                      <X size={12} />
                    </button>
                  )}
                </li>
              ))}
              {pendingLines.length > 4 && <li className="tiny muted">{t('dock.pending.more', { count: pendingLines.length - 4 })}</li>}
            </ul>
          )}
          <div className="prompt-dock__composer">
            {offered.length > 0 && (
              <ul className="composer__hints" role="listbox" aria-label={t('composer.suggestions')}>
                {offered.map((option, index) => (
                  <li key={option.label} role="option" aria-selected={index === picked}>
                    <button
                      type="button"
                      className={`composer__hint${index === picked ? ' is-picked' : ''}`}
                      onMouseDown={(event) => {
                        // Before the box loses focus, so the pick lands in it.
                        event.preventDefault()
                        setText(applySuggestion(text, option))
                        setPicked(0)
                      }}
                    >
                      <span className="mono">{option.label}</span>
                      <span className="tiny muted">{option.hint ?? (option.hintKey ? t(option.hintKey) : '')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div
              className="prompt-dock__field-resize"
              role="separator"
              aria-orientation="horizontal"
              aria-label={t('dock.fieldResize')}
              title={t('dock.fieldResize')}
              onPointerDown={(event) => {
                fieldDrag.current = { y: event.clientY, from: area.current?.offsetHeight ?? FIELD_MIN }
                ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
              }}
              onPointerMove={onFieldDrag}
              onPointerUp={(event) => {
                fieldDrag.current = null
                ;(event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId)
              }}
              onDoubleClick={() => {
                setFieldHeight(null)
                store(FIELD_HEIGHT_KEY, null)
              }}
            />
            <textarea
              ref={area}
              className="prompt-dock__text"
              rows={1}
              value={text}
              placeholder={kids ? t('kids.placeholder') : t('composer.placeholder')}
              aria-label={t('dock.placeholder')}
              onChange={(event) => {
                setText(event.target.value)
                setCursor(event.target.selectionStart ?? event.target.value.length)
                setPicked(0)
                setHideHints(false)
              }}
              onSelect={(event) => setCursor(event.currentTarget.selectionStart ?? 0)}
              onKeyDown={(event) => {
                if (offered.length > 0) {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault()
                    setPicked((picked + (event.key === 'ArrowDown' ? 1 : offered.length - 1)) % offered.length)
                    return
                  }
                  if ((event.key === 'Tab' || event.key === 'Enter') && !event.ctrlKey && !event.metaKey) {
                    event.preventDefault()
                    setText(applySuggestion(text, offered[picked] ?? offered[0]!))
                    setPicked(0)
                    return
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault()
                    setHideHints(true)
                    return
                  }
                }
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                  // Enter sends; Ctrl+Enter breaks the line (the owner's call, 16/10). Shift+Enter breaks too.
                  event.preventDefault()
                  const box = event.currentTarget
                  const at = box.selectionStart ?? text.length
                  const end = box.selectionEnd ?? at
                  setText(`${text.slice(0, at)}\n${text.slice(end)}`)
                  requestAnimationFrame(() => box.setSelectionRange(at + 1, at + 1))
                  return
                }
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  submit()
                }
              }}
            />
            {/* One line (51, D1): Super skills, who does it, when, and Enviar — with Analisar, Documentar and the two
                options behind the arrow beside it. An option switched on stays in sight as a chip, a click turns it off. */}
            <div className="prompt-dock__toolbar">
              {reviewFirst && (
                <button type="button" className="tiny prompt-dock__cadence prompt-dock__review" onClick={() => toggleReview(false)} title={t('dock.optionOff')}>
                  <Sparkles size={11} aria-hidden /> {t('dock.reviewFirst')} ×
                </button>
              )}
              {autoDoc && (
                <button type="button" className="tiny prompt-dock__cadence prompt-dock__review" onClick={() => toggleAutoDoc(false)} title={t('dock.autoDocInfo')}>
                  <FileText size={11} aria-hidden /> {t('dock.autoDoc')} ×
                </button>
              )}
              {cadence?.kind === 'At' && (
                <span className="row row--tight tiny prompt-dock__cadence">
                  {t('dock.cadence.at')}
                  <input
                    type="datetime-local"
                    className="input input--sm prompt-dock__when"
                    value={cadence.at}
                    aria-label={t('dock.cadence.at')}
                    onChange={(event) => setCadence({ kind: 'At', at: event.target.value })}
                  />
                  <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => setCadence(null)} title={t('dock.cadence.clear')} aria-label={t('dock.cadence.clear')}>×</button>
                </span>
              )}
              {cadence?.kind === 'Clock' && (
                <span className="row row--tight tiny prompt-dock__cadence">
                  <button type="button" className="tiny prompt-dock__cadence" onClick={() => setClockDraft(cadence.draft)} title={t('dock.cadence.clockEdit')}>
                    {describeSchedule(t, locale, { intervalMinutes: null, cron: cronOf(cadence.draft) })}
                  </button>
                  <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => setCadence(null)} title={t('dock.cadence.clear')} aria-label={t('dock.cadence.clear')}>×</button>
                </span>
              )}
              {cadence && cadence.kind !== 'At' && cadence.kind !== 'Clock' && (
                <button type="button" className="tiny prompt-dock__cadence" onClick={() => setCadence(null)} title={t('dock.cadence.clear')}>
                  {cadence.kind === 'UntilDone' ? t('dock.cadence.untilDone') : t('dock.cadence.every', { interval: formatInterval(t, cadence.every) })} ×
                </button>
              )}
              {agents > 1 && (
                <button type="button" className="tiny prompt-dock__cadence" onClick={() => { setAgents(1); setMates([]) }} title={t('dock.agents.clear')}>
                  {mates.length > 0
                    ? t('dock.agents.chipWith', { count: agents, names: mates.map((m) => m.name).join(', ') })
                    : t('dock.agents.chip', { count: agents })} ×
                </button>
              )}
              <span className="spacer" />
              {superSkillList.length > 0 && (
                <button type="button" className="btn btn--ghost btn--sm prompt-dock__super" onClick={() => setSuperSkills({ initial: null })} title={t('superSkills.hint')}>
                  <Wand2 size={13} aria-hidden /> {t('superSkills.button')}
                </button>
              )}
              <Dropdown
                className="prompt-dock__agents-menu"
                label={t('dock.agents.label')}
                align="end"
                buttonClassName={`btn btn--ghost btn--icon btn--sm${agents > 1 ? ' is-active' : ''}`}
                button={<Users size={14} />}
              >
                {(close) => (
                  <>
                    <DropdownItem label={t('dock.agents.single')} hint={t('dock.agents.singleHint')} onSelect={() => { setAgents(1); setMates([]); close() }} />
                    {[2, 3].map((n) => (
                      <DropdownItem key={n} label={t('dock.agents.several', { count: n })} hint={t('dock.agents.severalHint')} onSelect={() => { setAgents(n); setMates([]); close() }} />
                    ))}
                    {/* A teammate's agent (docs/refactor/15, phase 2): only who accepts team work and has the app or remote control on. */}
                    {callable.map((m) => {
                      const chosen = mates.some((x) => x.developerId === m.developerId)
                      return (
                        <DropdownItem
                          key={m.developerId}
                          label={t('dock.agents.mate', { name: m.name })}
                          hint={t('dock.agents.mateHint', { name: m.name })}
                          disabled={chosen || Math.max(agents, 1) >= 3}
                          onSelect={() => {
                            setMates([...mates, { developerId: m.developerId, name: m.name }])
                            setAgents(Math.max(agents, 1) + 1)
                            close()
                          }}
                        />
                      )
                    })}
                  </>
                )}
              </Dropdown>
              <Dropdown
                className="prompt-dock__cadence-menu"
                label={t('dock.cadence.label')}
                align="end"
                buttonClassName="btn btn--ghost btn--icon btn--sm"
                button={<Timer size={14} />}
              >
                {(close) => (
                  <>
                    <DropdownItem label={t('dock.cadence.now')} hint={t('dock.cadence.nowHint')} onSelect={() => { setCadence(null); close() }} />
                    <DropdownItem label={t('dock.cadence.untilDone')} hint={t('dock.cadence.untilDoneHint')} onSelect={() => { setCadence({ kind: 'UntilDone' }); close() }} />
                    <DropdownItem label={t('dock.cadence.atMenu')} hint={t('dock.cadence.atHint')} onSelect={() => { setCadence({ kind: 'At', at: nextHourLocal() }); close() }} />
                    {[30, 60, 180, 1440].map((every) => (
                      <DropdownItem key={every} label={t('dock.cadence.every', { interval: formatInterval(t, every) })} onSelect={() => { setCadence({ kind: 'Scheduled', every }); close() }} />
                    ))}
                    <DropdownItem label={t('dock.cadence.clockMenu')} hint={t('dock.cadence.clockHint')} onSelect={() => { setClockDraft(cadence?.kind === 'Clock' ? cadence.draft : DEFAULT_SCHEDULE); close() }} />
                  </>
                )}
              </Dropdown>
              {stoppable ? (
                <button
                  type="button"
                  className="prompt-dock__send is-stop"
                  onClick={stop}
                  disabled={stopQueued.isPending}
                  aria-label={t('dock.stop')}
                  title={t('dock.stop')}
                >
                  <Square size={11} fill="currentColor" />
                </button>
              ) : (
                <span className="prompt-dock__send-group">
                <button
                  type="button"
                  className="prompt-dock__send"
                  onClick={() => submit()}
                  disabled={!text.trim() || send.isPending}
                  aria-label={t('dock.send')}
                  title={`${t('dock.send')} (Enter)`}
                >
                  <ArrowUp size={15} />
                </button>
                <Dropdown
                  className="prompt-dock__as"
                  label={t('dock.sendOptions')}
                  align="end"
                  buttonClassName="btn btn--ghost btn--icon btn--sm prompt-dock__send-more"
                  button={<ChevronDown size={14} />}
                >
                  {(close) => (
                    <>
                      <DropdownItem icon={<Search size={14} />} label={t('dock.analyze')} hint={t('dock.analyzeHint')} disabled={!text.trim() || send.isPending} onSelect={() => { close(); sendAs('request.analyze') }} />
                      <DropdownItem icon={<FileText size={14} />} label={t('dock.document')} hint={t('dock.documentHint')} disabled={!text.trim() || send.isPending} onSelect={() => { close(); sendAs('request.prd') }} />
                      <div className="dropdown__sep" role="separator" />
                      <DropdownItem icon={reviewFirst ? <Check size={14} /> : <Sparkles size={14} />} label={t('dock.reviewFirst')} onSelect={() => { close(); toggleReview(!reviewFirst) }} />
                      <DropdownItem icon={autoDoc ? <Check size={14} /> : <FileText size={14} />} label={t('dock.autoDoc')} hint={t('dock.autoDocInfo')} onSelect={() => { close(); toggleAutoDoc(!autoDoc) }} />
                    </>
                  )}
                </Dropdown>
                </span>
              )}
            </div>
          </div>

          {notice ? (
            <p className={`tiny prompt-dock__notice${notice.ok ? '' : ' prompt-dock__notice--bad'}`} role="status">{notice.text}</p>
          ) : (
            <>
            {/* A first conversation's way in (docs/refactor/12-linguagem-leiga.md): a ready prompt that fills the
                box — never sends — while nothing was asked in this conversation yet. */}
            {mode === 'agent' && !kids && !fused && !text && repository && !here?.inConversation && !(here?.stream?.length) && (
              <div className="row row--tight row--wrap composer__starters">
                <span className="tiny muted">{t('composer.startHere')}</span>
                {(['explain', 'next'] as const).map((id) => (
                  <button key={id} type="button" className="btn btn--sm btn--ghost composer__starter" onClick={() => { setText(t(`composer.starter.${id}`)); area.current?.focus() }}>
                    {t(`composer.starter.${id}`)}
                  </button>
                ))}
              </div>
            )}
            {/* What goes with every request from here (docs/refactor/21): said, not hidden. */}
            {desktop && runsHere && withEvery.length > 0 && (
              <p className="tiny muted composer__with" title={t('dock.withEveryHint')}>
                {t('dock.withEvery', { list: withEvery.join(' · ') })}{' '}
                <button type="button" className="link-button" onClick={() => setTab('along')}>{t('dock.withEveryEdit')}</button>
              </p>
            )}
            <p className="tiny muted composer__help">{t(desktop ? 'composer.help' : 'composer.helpWeb')}</p>
            </>
          )}

          </div>

          {(showResult || fused) && (
            <div className="prompt-dock__result">
              {/* Fused, the answer alone: no tabs (docs/refactor/62). */}
              {!fused && (
              <div className="prompt-dock__tabs" role="tablist" aria-label={t('dock.result')}>
                {[...tabs, ...moreTabs.filter((item) => item.key === tab)].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.key}
                    className={`prompt-dock__tab${tab === item.key ? ' is-active' : ''}`}
                    onClick={() => setTab(item.key)}
                  >
                    {item.icon} {item.label}
                    {item.count !== undefined && item.count > 0 && <span className="prompt-dock__tab-count">{item.count}</span>}
                  </button>
                ))}
                {moreTabs.some((item) => item.key !== tab) && (
                  <Dropdown label={t('dock.moreTabs')} buttonClassName="prompt-dock__tab prompt-dock__tab--more" button={<><MoreHorizontal size={13} />{moreCount > 0 && <span className="prompt-dock__tab-count">{moreCount}</span>}</>}>
                    {(close) => moreTabs.filter((item) => item.key !== tab).map((item) => (
                      <DropdownItem key={item.key} icon={item.icon} label={item.label} side={item.count ? String(item.count) : undefined} onSelect={() => { setTab(item.key); close() }} />
                    ))}
                  </Dropdown>
                )}
              </div>
              )}
              <div className="prompt-dock__pane" role="tabpanel">
                {fused && tab === 'reply' && !showStream && !replyText && <p className="tiny muted prompt-dock__fused-empty">{t('dock.fusedEmpty')}</p>}
                {tab === 'reply' && (showStream && here ? (
                  <>
                    <RunStream items={here.stream ?? []} prompt={here.streamPrompt} working={hereWorking} waiting={here.waitingForFolder === true} />
                    {/* What the AI made, a click away once it is done (51, N6). */}
                    {!hereWorking && <FileShortcuts text={(here.stream ?? []).map((item) => (item.kind === 'tool' ? '' : item.text)).join('\n')} folder={hereFolder} />}
                  </>
                ) : (
                  <>
                    {resultIsOld && result && lastRun && (
                      <p className="tiny muted prompt-dock__old-reply">
                        {t('dock.oldReply', { when: formatRelative(result.finishedAt, locale) })}
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => clearResult(lastRun.repository, viewLane)}>{t('dock.clearReply')}</button>
                      </p>
                    )}
                    {/* Fused and nothing yet: the hint above says it, once. */}
                    {!(fused && !replyText) && <ReplyText text={replyText} quiet={resultIsOld} />}
                    <FileShortcuts text={replyText} folder={result?.folder ?? (lastRun ? folderFor(lastRun.repository) : null)} />
                  </>
                ))}
                {tab === 'changes' && (result?.checkpoint && lastRun && (result.folder ?? folderFor(lastRun.repository)) ? (
                  <ChangesPanel folder={(result.folder ?? folderFor(lastRun.repository))!} checkpoint={result.checkpoint} initial={result.changes ?? []} shared={result.shared} />
                ) : (
                  <FileList files={[...(result?.created ?? []), ...(result?.modified ?? [])]} empty={t('changes.none')} repository={lastRun?.repository} />
                ))}
                {tab === 'log' && <pre className="tiny">{(lastRun?.log ?? []).join('\n') || t('dock.noLog')}</pre>}
                {tab === 'task' && (
                  <div className="stack" style={{ gap: 10 }}>
                    {taskId && <TaskConversation id={taskId} />}
                    <AgentNotes notes={notes} />
                  </div>
                )}
                {tab === 'queue' && <QueueList items={queued} onCancel={(id) => cancelQueued.mutate(id)} onUndo={(id) => undoQueued.mutate(id)} undoError={undoQueued.error} />}
                {tab === 'preview' && preview && <PreviewPanel url={preview} />}
                {tab === 'along' && (
                  <BeforeEachCommand
                    desktop={desktop}
                    pinned={pinned ?? []}
                    local={desktop ? readLocalInstructions() : ''}
                    permission={desktop ? readRunnerSettings().permission : null}
                    skills={desktop ? (skillsHere ?? []).filter((x) => x.enabled) : null}
                    folder={hereFolder}
                    listFiles={listRepoFiles}
                    readFile={repoRead}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {shown && !full && !fused && (
        <div
          className="prompt-dock__resize"
          role="separator"
          aria-label={t('dock.resize')}
          title={t('dock.resize')}
          onPointerDown={(event) => startDrag('size', event)}
          onPointerMove={onDrag}
          onPointerUp={endDrag}
        />
      )}

          {who.dialog}
          {superSkills && superSkillList.length > 0 && (
            <SuperSkills
              skills={superSkillList}
              promptFor={(key) => teamPrompt(key) ?? catalog?.prompts.find((p) => p.key === key)?.body ?? null}
              agent={desktop ? readRunnerSettings().agent : null}
              agentLabel={desktop ? agentName(readRunnerSettings().agent) : ''}
              desktop={desktop}
              here={desktop && skillsFolder ? (skillsHere ?? []) : null}
              pickFile={pickFilePath}
              initial={superSkills.initial}
              onSend={(body, shown) => sendCommand(body, shown)}
              onClose={() => setSuperSkills(null)}
            />
          )}
          {reviewing && (
            <PromptReviewDialog
              prompt={text}
              autoRun
              onClose={() => setReviewing(false)}
              onApplyOriginal={(original) => {
                setReviewing(false)
                send.mutate(original)
              }}
              onApplySuggestion={(improved) => {
                setText(improved)
                setReviewing(false)
                send.mutate(improved)
              }}
            />
          )}
      {clockDraft && (
        <Modal title={t('dock.cadence.clockTitle')} onClose={() => setClockDraft(null)}>
          <div className="stack">
            <p className="small muted" style={{ margin: 0 }}>{t('dock.cadence.clockIntro')}</p>
            <SchedulePicker value={clockDraft} onChange={setClockDraft} />
            <div className="row row--end">
              <button type="button" className="btn btn--ghost" onClick={() => setClockDraft(null)}>{t('common.cancel')}</button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  // "A cada" chosen here is the ordinary loop; any clock mode keeps the draft.
                  setCadence(clockDraft.mode === 'interval' ? { kind: 'Scheduled', every: clockDraft.minutes } : { kind: 'Clock', draft: clockDraft })
                  setClockDraft(null)
                }}
              >
                {t('dock.cadence.clockUse')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  )
}

const STATUS_TONE: Record<QueuedPrompt['status'], string> = {
  Pending: 'is-wait',
  Running: 'is-run',
  Done: 'is-ok',
  Failed: 'is-bad',
  Cancelled: 'is-off',
}

/** The latest prompts in the queue, newest first: what each is doing, and a way to withdraw one not yet started. */
function QueueList({ items, onCancel, onUndo, undoError }: { items: QueuedPrompt[]; onCancel: (id: string) => void; onUndo: (id: string) => void; undoError?: unknown }) {
  const { t } = useI18n()
  if (items.length === 0) return <p className="tiny muted" style={{ margin: 0 }}>{t('dock.queueEmpty')}</p>
  const undoCode = undoError instanceof ApiError ? undoError.code : undoError ? 'other' : null
  return (
    <>
    {undoCode && <p className="field__error" role="alert">{t(['undo_not_latest', 'undo_no_checkpoint', 'undo_already'].includes(undoCode) ? `dock.undo.error.${undoCode}` : 'error.generic')}</p>}
    <ul className="prompt-dock__queue">
      {items.slice(0, 10).map((item) => {
        const pendingWork = item.status === 'Done' && /DEVPULSE-STATUS:\s*PENDING/i.test(item.result ?? '')
        return (
          <li key={item.id} title={item.result ?? item.prompt}>
            <span className={`prompt-dock__status ${STATUS_TONE[item.status]}`}>
              {pendingWork ? t('dock.queue.DonePending') : t(`dock.queue.${item.status}`)}
            </span>
            <span className="tiny prompt-dock__queue-text">{item.prompt.split('\n')[0]}</span>
            {laneOf(item) > 1 && <span className="tiny prompt-dock__lane-chip">{t('dock.lane', { n: laneOf(item) })}</span>}
            {item.repository && <span className="tiny muted mono">{item.repository}</span>}
            {/* Follow it from anywhere: the computer running it reports to this session, live. */}
            {item.workSessionId && (
              <Link to={`/work/${item.workSessionId}`} className="tiny prompt-dock__watch">
                {item.status === 'Running' ? t('dock.watchLive') : t('dock.seeSession')}
              </Link>
            )}
            {item.status === 'Pending' && (
              <button type="button" className="btn btn--ghost btn--icon btn--sm" aria-label={t('dock.queueCancel')} title={t('dock.queueCancel')} onClick={() => onCancel(item.id)}>
                <X size={12} />
              </button>
            )}
            {item.changes && item.changes.length > 0 && <TurnChanges item={item} onUndo={onUndo} />}
          </li>
        )
      })}
    </ul>
    </>
  )
}

/**
 * What a turn changed, from the computer that ran it (docs/refactor/02): files and +/−, and "Desfazer este turno",
 * which that computer does on its next ask. Asked twice, since it throws the turn's work away.
 */
function TurnChanges({ item, onUndo }: { item: QueuedPrompt; onUndo: (id: string) => void }) {
  const { t } = useI18n()
  const [sure, setSure] = useState(false)
  const files = item.changes ?? []
  const added = files.reduce((n, f) => n + f.added, 0)
  const deleted = files.reduce((n, f) => n + f.deleted, 0)
  const why = item.undoResult?.startsWith('failed:') ? item.undoResult.slice('failed:'.length) : ''
  const restored = item.undoResult?.startsWith('ok:') ? Number(item.undoResult.slice('ok:'.length)) : files.length
  return (
    <div className="prompt-dock__changes">
      <details>
        <summary className="tiny">
          {t('dock.changes.summary', { count: files.length })} <span className="ok-text">+{added}</span> <span className="danger-text">−{deleted}</span>
        </summary>
        <ul className="prompt-dock__files">
          {files.map((f) => (
            <li key={f.path} className="mono tiny">
              <span className="muted">{f.status}</span> {f.path} <span className="ok-text">+{f.added}</span> <span className="danger-text">−{f.deleted}</span>
            </li>
          ))}
        </ul>
      </details>
      {item.undo === 'requested' && <span className="tiny muted">{t('dock.undo.requested')}</span>}
      {item.undo === 'done' && <span className="tiny ok-text">{t('dock.undo.done', { count: restored })}</span>}
      {item.undo === 'failed' && <span className="tiny danger-text">{t('dock.undo.failed', { why })}</span>}
      {item.canUndo && !item.undo && (
        sure ? (
          <span className="row row--tight">
            <button type="button" className="btn btn--sm btn--danger" onClick={() => { setSure(false); onUndo(item.id) }}>{t('dock.undo.confirm')}</button>
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => setSure(false)}>{t('common.cancel')}</button>
          </span>
        ) : (
          <button type="button" className="btn btn--sm" onClick={() => setSure(true)} title={t('dock.undo.hint')}>{t('dock.undo.button')}</button>
        )
      )}
    </div>
  )
}

function FileList({ files, empty, repository }: { files: string[]; empty: string; repository?: string }) {
  if (files.length === 0) return <p className="tiny muted" style={{ margin: 0 }}>{empty}</p>
  return (
    <ul className="prompt-dock__files">
      {files.map((file) => (
        <li key={file} className="mono tiny" title={repository ? `${repository}/${file}` : file}>{file}</li>
      ))}
    </ul>
  )
}


function agentName(agent: LocalAgentId): string {
  return AGENT_NAMES[agent] ?? agent
}

/**
 * A run on this machine as it happens, the way an agent's own panel shows it: the prompt, what the
 * agent writes, and a line for each tool it uses — kept scrolled to the newest while it works.
 */
function RunStream({ items, prompt, working, waiting = false }: { items: StreamItem[]; prompt?: string; working: boolean; waiting?: boolean }) {
  const { t } = useI18n()
  const end = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (working) end.current?.scrollIntoView({ block: 'end' })
  }, [items, working])
  return (
    <div className="run-stream" aria-live="polite" aria-busy={working}>
      {prompt && <div className="run-stream__prompt tiny">{prompt}</div>}
      {items.map((item, index) =>
        item.kind === 'tool' ? (
          <div key={index} className="run-stream__tool tiny">
            <Wrench size={11} aria-hidden /> <strong>{item.name}</strong>
            {item.detail && <span className="mono muted truncate">{item.detail}</span>}
          </div>
        ) : (
          // The agent writes Markdown: shown as such — headings, bold, lists, code — like a document.
          <div key={index} className="run-stream__text run-stream__md small"><Markdown source={item.text} /></div>
        ),
      )}
      {waiting && !working && (
        <div className="run-stream__working tiny muted">
          <Hourglass size={12} aria-hidden /> {t('dock.waitingFolder')}
        </div>
      )}
      {working && (
        <div className="run-stream__working tiny muted">
          <Loader2 size={12} className="spin" aria-hidden /> {t('dock.agentWorking')}
        </div>
      )}
      <div ref={end} />
    </div>
  )
}

function isLocalAgentId(agent: string): agent is LocalAgentId {
  return agent === 'claude' || agent === 'claude-2' || agent === 'codex' || agent === 'gemini' || agent === 'opencode'
}

/**
 * The project a command sent through the queue goes to — or "Qualquer projeto", and the computer that takes it chooses
 * (docs/refactor/29): by the name in the words, else by asking its agent; never one at random.
 */
function ProjectPick({ linked, chosen, onChoose }: { linked: Array<{ id: string; name: string }>; chosen: string | null; onChoose: (id: string | null) => void }) {
  const { t } = useI18n()
  return (
    <select
      className="prompt-dock__project"
      value={chosen ?? ''}
      onChange={(event) => onChoose(event.target.value || null)}
      aria-label={t('dock.projectPick')}
      title={chosen ? undefined : t('dock.anyProjectHint')}
    >
      <option value="">{t('dock.anyProject')}</option>
      {linked.map((r) => (
        <option key={r.id} value={r.id}>{t('dock.inRepo', { name: r.name })}</option>
      ))}
    </select>
  )
}

/**
 * An answer that came back (from the queue, from remote control): the agent's Markdown shown as such, and the
 * status line it ends with — Vephus's own marker — said in words, never shown raw.
 */
function ReplyText({ text, quiet = false }: { text: string; quiet?: boolean }) {
  const { t } = useI18n()
  if (!text.trim()) return <p className="tiny muted">{t('dock.noReply')}</p>
  const { body, pending } = readableReply(text, t)
  return (
    <div className="run-stream__md small prompt-dock__answer">
      {body && <Markdown source={body} />}
      {pending && <p className={quiet ? 'tiny muted' : 'prompt-dock__needs tiny'}>{t('dock.needsYou', { reason: pending })}</p>}
    </div>
  )
}
